import { useEffect, useMemo, useState } from 'react';

// „Betongold" — passend: Goldmünzen/Geldsack + Immobilien (Haus/Schlüssel/Ziegel) + Glitzer.
const EMOJIS = ['🪙', '💰', '🏠', '🔑', '✨', '🧱', '🪙', '🏆'];
const DURATION_MS = 3000;

interface Drop {
  left: number;
  delay: number;
  dur: number;
  emoji: string;
  size: number;
}

/**
 * Kurze Feier-Animation für Top-Treffer (≥ 95%): ein Regen aus Goldmünzen, Häusern & Schlüsseln
 * plus ein „Betongold-Treffer!"-Banner. Blockiert das Swipen NICHT (pointer-events: none) und
 * verschwindet nach ~3 s von allein. Feuert, wenn `trigger` sich auf eine neue Objekt-ID ändert.
 */
export function GoldRain({ trigger }: { trigger: number | undefined }): JSX.Element | null {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (trigger === undefined) return;
    setActive(true);
    const t = setTimeout(() => setActive(false), DURATION_MS);
    return () => clearTimeout(t);
  }, [trigger]);

  // Tropfen einmal pro Trigger würfeln (stabil über die Laufzeit, kein Neu-Würfeln bei Re-Render).
  const drops = useMemo<Drop[]>(() => {
    return Array.from({ length: 28 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.9,
      dur: 1.7 + Math.random() * 1.5,
      emoji: EMOJIS[i % EMOJIS.length] ?? '🪙',
      size: 18 + Math.random() * 22,
    }));
  }, [trigger]);

  if (!active) return null;

  return (
    <div
      aria-hidden
      style={{ position: 'fixed', inset: 0, zIndex: 200, pointerEvents: 'none', overflow: 'hidden' }}
    >
      <style>{`
        @keyframes bg-fall {
          0% { transform: translateY(-14vh) rotate(0deg); opacity: 0; }
          8% { opacity: 1; }
          100% { transform: translateY(114vh) rotate(360deg); opacity: 1; }
        }
        @keyframes bg-pop {
          0% { transform: scale(0.4) translateY(14px); opacity: 0; }
          22% { transform: scale(1.12) translateY(0); opacity: 1; }
          78% { transform: scale(1) translateY(0); opacity: 1; }
          100% { transform: scale(0.98) translateY(-6px); opacity: 0; }
        }
      `}</style>
      {drops.map((d, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            top: 0,
            left: `${d.left}%`,
            fontSize: d.size,
            animation: `bg-fall ${d.dur}s linear ${d.delay}s forwards`,
          }}
        >
          {d.emoji}
        </span>
      ))}
      <div style={{ position: 'absolute', top: '30%', left: 0, right: 0, textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-block',
            animation: 'bg-pop 3s ease forwards',
            background: 'linear-gradient(135deg, #fef08a 0%, #f59e0b 50%, #d97706 100%)',
            color: '#7c2d12',
            fontWeight: 900,
            padding: '10px 22px',
            borderRadius: 16,
            boxShadow: '0 10px 34px rgba(217,119,6,0.6)',
            fontSize: '1.3rem',
            letterSpacing: '-0.02em',
          }}
        >
          🏆 Betongold-Treffer!
          <div style={{ fontSize: '0.8rem', fontWeight: 700, opacity: 0.85 }}>
            95%+ — da klingelt der Beton 🧱
          </div>
        </div>
      </div>
    </div>
  );
}
